import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

/** Matches mobile `HomeScreen` dead zone / accuracy gates for the user dot. */
const MIN_GPS_DISPLAY_MOVE_METERS = 15;

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const r = (d) => (d * Math.PI) / 180;
  const dLat = r(lat2 - lat1);
  const dLon = r(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(r(lat1)) * Math.cos(r(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/**
 * Reject poor fixes and fused “teleports” (same idea as mobile `shouldApplyGpsUpdate`).
 * `prevRef` holds last accepted fix: { lat, lng, t }.
 */
function shouldApplyBrowserGpsUpdate(latitude, longitude, accuracy, prevRef) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const acc = accuracy != null ? Number(accuracy) : null;
  if (acc != null && Number.isFinite(acc) && acc > 4000) return false;
  if (acc != null && Number.isFinite(acc) && acc > 85) return false;

  const prev = prevRef.current;
  if (prev.lat == null || prev.lng == null) {
    prevRef.current = { lat, lng, t: Date.now() };
    return true;
  }
  const dt = (Date.now() - prev.t) / 1000;
  const dist = haversineMeters(prev.lat, prev.lng, lat, lng);
  if (dist > 250 && dt < 8) return false;
  if (dist < MIN_GPS_DISPLAY_MOVE_METERS) return false;
  prevRef.current = { lat, lng, t: Date.now() };
  return true;
}

function geolocationErrorMessage(err) {
  if (!err || typeof err.code !== 'number') {
    return 'Could not read your location. Please try again.';
  }
  // GeolocationPositionError codes (1/2/3); avoid relying on statics on `err`.
  switch (err.code) {
    case 1:
      return 'Location permission denied. Enable access in your browser settings to use this.';
    case 2:
      return 'Location is unavailable right now. Try again or check your device settings.';
    case 3:
      return 'Location request timed out. Try again in an open area.';
    default:
      return 'An error occurred while reading your location.';
  }
}

const USER_LOCATION_WATCH_OPTIONS = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 10000,
};

const LiveMap = ({ isVisible }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylinesRef = useRef([]);
  const markersRef = useRef({});
  const animationStateRef = useRef({});
  const rafIdRef = useRef(null);
  const stopMarkersRef = useRef([]);
  const userLocationMarkerRef = useRef(null);

  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [visibleRoutes, setVisibleRoutes] = useState(new Set()); // Using Set but initialized properly
  const [activeRouteIds, setActiveRouteIds] = useState(new Set()); // Routes with buses
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [showUserLocation, setShowUserLocation] = useState(false);
  const [userLocationError, setUserLocationError] = useState('');
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null); // { stopId, name, routes }
  const [stopArrivals, setStopArrivals] = useState([]); // Array of { routeId, routeName, color, arrivals: [] }
  const [loadingArrivals, setLoadingArrivals] = useState(false);

  const GT_CENTER = { lat: 33.7756, lng: -84.3963 };
  const GT_BOUNDS = {
    north: 33.7856,
    south: 33.7656,
    east: -84.3863,
    west: -84.4063
  };

  const POLL_INTERVAL = 3000;

  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsGoogleMapsLoaded(true);
    } else {
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          setIsGoogleMapsLoaded(true);
          clearInterval(checkGoogleMaps);
        }
      }, 100);
      return () => clearInterval(checkGoogleMaps);
    }
  }, []);

  // Handle visibility changes to resize map
  useEffect(() => {
    if (isVisible && mapInstanceRef.current && window.google) {
      window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
      // Re-center map if needed
      if (mapInstanceRef.current.getCenter()) {
        mapInstanceRef.current.setCenter(mapInstanceRef.current.getCenter());
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (isGoogleMapsLoaded) {
      initializeMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapsLoaded]);

  // Re-render routes when visibility OR activeRouteIds changes
  useEffect(() => {
    if (mapInstanceRef.current && routes.length > 0) {
      renderRoutesActual();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes, visibleRoutes, activeRouteIds]);

  useEffect(() => {
    if (mapInstanceRef.current && vehicles.length >= 0) {
      updateVehicleMarkers(vehicles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, visibleRoutes]);

  // Calculate active routes whenever vehicles change
  useEffect(() => {
    const counts = {};
    vehicles.forEach(v => {
      const rid = String(v.routeId || v.RouteID || v.route_id);
      if (rid) counts[rid] = (counts[rid] || 0) + 1;
    });
    const activeIds = new Set(Object.keys(counts).filter(rid => counts[rid] > 0));
    setActiveRouteIds(activeIds);

    // Also update visibleRoutes to only include active ones by default
    // But KEEP any that user has toggled on/off
    // Actually, visibleRoutes should START as active routes only
    // On first load, set visibleRoutes = activeRouteIds
    // But we need to be careful not to reset user toggles
    // For simplicity: visibleRoutes defaults to all active, user can toggle
  }, [vehicles]);

  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapInstanceRef.current) return;

    let intervalId = null;

    const fetchVehicles = async () => {
      if (document.hidden) return;

      try {
        const hostname = window.location.hostname;
        const apiUrl = (hostname === 'localhost' || hostname === '127.0.0.1')
          ? '/api/RouteSearch'
          : 'https://buzzbus-production.up.railway.app/api/RouteSearch';

        const response = await axios.get(`${apiUrl}/map-vehicles`);
        const activeVehicles = response.data || [];
        const validVehicles = activeVehicles.filter(v => v.vehicleId || v.id || v.VehicleID);
        setVehicles(validVehicles);
      } catch (err) {
        console.error("Error fetching vehicles:", err);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        fetchVehicles();
        if (!intervalId) {
          intervalId = setInterval(fetchVehicles, POLL_INTERVAL);
        }
      }
    };

    fetchVehicles();
    intervalId = setInterval(fetchVehicles, POLL_INTERVAL);
    animate();

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapsLoaded]);

  // Live user location (watchPosition), aligned with mobile `watchPositionAsync` + GPS filters.
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google) return;

    let watchId = null;
    let cancelled = false;
    const gpsPrevRef = { current: { lat: null, lng: null, t: 0 } };

    const createOrUpdateUserMarker = (userPos) => {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setPosition(userPos);
        return;
      }
      userLocationMarkerRef.current = new window.google.maps.Marker({
        position: userPos,
        map: mapInstanceRef.current,
        title: 'Your Location',
        icon: {
          url:
            'data:image/svg+xml,' +
            encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                      <circle cx="20" cy="20" r="18" fill="#3B82F6" fill-opacity="0.2">
                        <animate attributeName="r" values="10;18;10" dur="1.5s" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" values="0.4;0.1;0.4" dur="1.5s" repeatCount="indefinite"/>
                      </circle>
                      <circle cx="20" cy="20" r="8" fill="#3B82F6" stroke="#FFFFFF" stroke-width="3"/>
                    </svg>
                  `),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        },
        zIndex: 1000,
      });
    };

    if (!showUserLocation) {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null);
        userLocationMarkerRef.current = null;
      }
      return undefined;
    }

    if (!navigator.geolocation) {
      setUserLocationError('Geolocation is not supported by this browser.');
      setShowUserLocation(false);
      return undefined;
    }

    setUserLocationError('');
    gpsPrevRef.current = { lat: null, lng: null, t: 0 };

    const onPosition = (position) => {
      if (cancelled) return;
      const { latitude, longitude, accuracy } = position.coords;
      if (
        !shouldApplyBrowserGpsUpdate(latitude, longitude, accuracy, gpsPrevRef)
      ) {
        return;
      }
      createOrUpdateUserMarker({ lat: latitude, lng: longitude });
    };

    const onError = (err) => {
      if (cancelled) return;
      console.error('User location watch error:', err);
      setUserLocationError(geolocationErrorMessage(err));
      setShowUserLocation(false);
    };

    watchId = navigator.geolocation.watchPosition(
      onPosition,
      onError,
      USER_LOCATION_WATCH_OPTIONS
    );

    return () => {
      cancelled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null);
        userLocationMarkerRef.current = null;
      }
    };
  }, [showUserLocation, isGoogleMapsLoaded]);

  const initializeMap = () => {
    if (!mapRef.current) return;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: GT_CENTER,
      zoom: 15,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      gestureHandling: 'greedy', // Allow single-finger panning on mobile
      styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
    });

    mapInstanceRef.current.fitBounds(new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(GT_BOUNDS.south, GT_BOUNDS.west),
      new window.google.maps.LatLng(GT_BOUNDS.north, GT_BOUNDS.east)
    ));

    loadRoutes();
  };

  const loadRoutes = async () => {
    const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? '/api/RouteSearch'
      : 'https://buzzbus-production.up.railway.app/api/RouteSearch';

    try {
      setLoading(true);
      const routesResponse = await axios.get(`${apiUrl}/map-routes`);
      const allRoutes = routesResponse.data;
      setRoutes(allRoutes);

      // Don't set visibleRoutes here - let it be set after vehicles load
      // Initialize to empty, will be populated when activeRouteIds updates
      // Actually, set to all initially, but renderRoutesActual will filter by activeRouteIds
      const defaultVisible = new Set(allRoutes.map(route => String(route.routeId)).filter(id => id));
      setVisibleRoutes(defaultVisible);

    } catch (err) {
      setError(`Failed to load items: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderRoutesActual = () => {
    polylinesRef.current.forEach(p => p.setMap(null));
    polylinesRef.current = [];
    stopMarkersRef.current.forEach(m => m.setMap(null));
    stopMarkersRef.current = [];

    routes.forEach(route => {
      const rIdStr = String(route.routeId);

      // ONLY draw if: route is visible AND route has active buses
      if (!visibleRoutes.has(rIdStr)) return;
      if (!activeRouteIds.has(rIdStr)) return; // KEY FIX: Only draw active routes

      if (route.encodedPolyline) {
        const decodedPath = window.google.maps.geometry.encoding.decodePath(route.encodedPolyline);
        const polyline = new window.google.maps.Polyline({
          path: decodedPath,
          geodesic: true,
          strokeColor: route.mapLineColor || '#000000',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map: mapInstanceRef.current
        });
        polylinesRef.current.push(polyline);
      }
      route.stops?.forEach(stop => {
        if (stop.showDefaultedOnMap) {
          const marker = new window.google.maps.Marker({
            position: { lat: stop.latitude, lng: stop.longitude },
            map: mapInstanceRef.current,
            title: stop.description,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: '#FFFFFF',
              fillOpacity: 1,
              strokeColor: route.mapLineColor || '#000000',
              strokeWeight: 2
            }
          });

          // Add click handler
          marker.addListener('click', () => {
            handleStopClick({
              stopId: stop.routeStopId,  // Use routeStopId for API call
              name: stop.description,
              routeId: route.routeId,
              routeName: route.description,
              routeColor: route.mapLineColor || '#6366f1'
            });
          });

          stopMarkersRef.current.push(marker);
        }
      });
    });
  };

  // Handle stop click - fetch ETAs
  const handleStopClick = async (stop) => {
    setSelectedStop(stop);
    setSheetExpanded(true);
    setLoadingArrivals(true);
    setStopArrivals([]);

    try {
      const hostname = window.location.hostname;
      const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('ngrok');
      const apiUrl = isLocal
        ? '/api/RouteSearch'
        : 'https://buzzbus-production.up.railway.app/api/RouteSearch';

      const response = await axios.get(`${apiUrl}/stop-arrivals/${stop.routeId}/${stop.stopId}`);
      const data = response.data;

      setStopArrivals([{
        routeId: stop.routeId,
        routeName: stop.routeName,
        color: stop.routeColor,
        arrivals: data.arrivals || []
      }]);
    } catch (err) {
      console.error('Error fetching stop arrivals:', err);
      setStopArrivals([]);
    } finally {
      setLoadingArrivals(false);
    }
  };

  // Clear stop selection
  const clearStopSelection = () => {
    setSelectedStop(null);
    setStopArrivals([]);
  };

  const updateVehicleMarkers = (activeVehicles) => {
    if (!mapInstanceRef.current) return;
    const currentIds = new Set();
    const now = performance.now();

    const routeColorMap = {};
    routes.forEach(r => { if (r.routeId) routeColorMap[String(r.routeId)] = r.mapLineColor || '#808080'; });

    activeVehicles.forEach(v => {
      const vId = v.vehicleId || v.id || v.VehicleID;
      if (!vId) return;

      const routeId = v.routeId || v.RouteID || v.route_id;
      if (!visibleRoutes.has(String(routeId))) return;

      currentIds.add(String(vId));

      const targetLat = Number(v.latitude || v.Latitude);
      const targetLng = Number(v.longitude || v.Longitude);
      const heading = Number(v.heading || v.Heading || 0);
      const color = routeColorMap[String(routeId)] || '#808080';

      if (markersRef.current[vId]) {
        const marker = markersRef.current[vId];
        const currentPos = marker.getPosition();
        if (Math.abs(currentPos.lat() - targetLat) > 0.000001 || Math.abs(currentPos.lng() - targetLng) > 0.000001) {
          animationStateRef.current[vId] = {
            startLat: currentPos.lat(),
            startLng: currentPos.lng(),
            endLat: targetLat,
            endLng: targetLng,
            startTime: now,
          };
          marker.setIcon(createIcon(color, heading));
        }
        if (marker.getMap() === null) marker.setMap(mapInstanceRef.current);
      } else {
        const marker = new window.google.maps.Marker({
          position: { lat: targetLat, lng: targetLng },
          map: mapInstanceRef.current,
          title: `Bus ${vId}`,
          icon: createIcon(color, heading),
          zIndex: 999
        });
        markersRef.current[vId] = marker;

        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div style="padding: 5px;"><h4 style="margin:0">${v.name || vId}</h4><p style="margin:0;font-size:12px">Route: ${routeId}</p></div>`
        });
        marker.addListener('click', () => infoWindow.open(mapInstanceRef.current, marker));
      }
    });

    Object.keys(markersRef.current).forEach(id => {
      if (!currentIds.has(String(id))) {
        markersRef.current[id].setMap(null);
        delete markersRef.current[id];
        delete animationStateRef.current[id];
      }
    });
  };

  const createIcon = (color, rotation) => {
    const d = 'M 12 2 L 22 22 L 12 17 L 2 22 Z';
    const iconUrl =
      'data:image/svg+xml;charset=UTF-8,' +
      encodeURIComponent(`
        <svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(12,12) rotate(${rotation}) translate(-12,-12)">
            <path d="${d}" fill="${color}" stroke="#111827" stroke-width="3" stroke-linejoin="round"/>
            <path d="${d}" fill="none" stroke="#ffffff" stroke-width="1.25" stroke-linejoin="round" opacity="0.95"/>
          </g>
        </svg>
      `);
    return {
      url: iconUrl,
      scaledSize: new window.google.maps.Size(36, 36),
      anchor: new window.google.maps.Point(18, 18),
    };
  };

  const animate = () => {
    rafIdRef.current = requestAnimationFrame(animate);
    const now = performance.now();

    Object.keys(animationStateRef.current).forEach(vId => {
      const state = animationStateRef.current[vId];
      const marker = markersRef.current[vId];
      if (!state || !marker) return;

      const elapsed = now - state.startTime;
      const progress = Math.min(elapsed / POLL_INTERVAL, 1.0);

      const lat = state.startLat + (state.endLat - state.startLat) * progress;
      const lng = state.startLng + (state.endLng - state.startLng) * progress;

      marker.setPosition({ lat, lng });
      if (progress >= 1.0) delete animationStateRef.current[vId];
    });
  };

  const toggleRoute = (routeId) => {
    const rIdStr = String(routeId);
    const newVisibleRoutes = new Set(visibleRoutes);
    if (newVisibleRoutes.has(rIdStr)) newVisibleRoutes.delete(rIdStr);
    else newVisibleRoutes.add(rIdStr);
    setVisibleRoutes(newVisibleRoutes);
  };

  const refreshData = () => loadRoutes();

  const getRouteColorName = (description, color) => {
    const descUpper = (description || '').toUpperCase();
    if (descUpper.includes('RED')) return 'RED';
    if (descUpper.includes('BLUE')) return 'BLUE';
    if (descUpper.includes('GREEN')) return 'GREEN';
    if (descUpper.includes('EMORY')) return 'EMORY';
    if (descUpper.includes('NIGHT') && descUpper.includes('GOLD')) return 'NIGHT GOLD';
    if (descUpper.includes('GOLD') || descUpper.includes('TROLLEY') || descUpper.includes('TECH TROLLEY')) return 'GOLD';
    if (descUpper.includes('CLOUGH')) return 'CLOUGH';
    if (descUpper.includes('NORTHSIDE')) return 'NORTHSIDE';
    if (descUpper.includes('NASA')) return 'NASA/SCIENCE';
    if (descUpper.includes('CHARTER')) return 'CHARTER';

    if (color) {
      const c = color.toLowerCase();
      if (c.includes('dc2626') || c.includes('c8102e')) return 'RED';
      if (c.includes('1e40af') || c.includes('0033a0')) return 'BLUE';
    }
    return null;
  };

  // Count vehicles per route
  const routeVehicleCounts = {};
  vehicles.forEach(v => {
    const rid = v.routeId || v.RouteID || v.route_id;
    if (rid) routeVehicleCounts[String(rid)] = (routeVehicleCounts[String(rid)] || 0) + 1;
  });

  // Only show routes with active buses
  const activeRoutesData = routes
    .map(route => {
      const detectedName = getRouteColorName(route.description, route.mapLineColor);
      const finalName = detectedName || route.description || route.routeName || `Route ${route.routeId}`;

      return {
        routeId: route.routeId,
        description: route.description || route.routeName || `Route ${route.routeId}`,
        displayName: finalName,
        color: route.mapLineColor || '#6366f1',
        vehicleCount: routeVehicleCounts[String(route.routeId)] || 0
      };
    })
    .filter(r => r.vehicleCount > 0);

  if (!isGoogleMapsLoaded) return <div className="map-loading"><p>Loading Google Maps...</p></div>;

  return (
    <div style={{ position: 'relative', height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
      {/* Full-screen Map */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(241, 245, 249, 0.9)',
            zIndex: 5
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e2e8f0',
              borderTopColor: '#EAB308',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        {error && <div className="map-error"><p>{error}</p><button onClick={refreshData}>Retry</button></div>}
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      </div>

      {/* Show Location Checkbox - floating */}
      <div
        style={{
          position: 'absolute',
          top: '0.75rem',
          right: '0.75rem',
          zIndex: 10,
          maxWidth: 'min(280px, calc(100vw - 1.5rem))',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '0.35rem',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            fontSize: '0.75rem',
            color: '#1e293b',
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderRadius: '0.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={showUserLocation}
            onChange={(e) => {
              const next = e.target.checked;
              setShowUserLocation(next);
              if (!next) setUserLocationError('');
            }}
            style={{ width: '0.875rem', height: '0.875rem', accentColor: '#3B82F6', cursor: 'pointer' }}
          />
          Show my location
        </label>
        {userLocationError ? (
          <div
            role="alert"
            style={{
              padding: '0.4rem 0.65rem',
              fontSize: '0.7rem',
              lineHeight: 1.35,
              color: '#b91c1c',
              backgroundColor: 'rgba(254, 242, 242, 0.98)',
              borderRadius: '0.4rem',
              boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            }}
          >
            {userLocationError}
          </div>
        ) : null}
      </div>

      {/* Bottom Sheet */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ffffff',
        borderTopLeftRadius: '1rem',
        borderTopRightRadius: '1rem',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        transition: 'transform 0.3s ease-out',
        transform: sheetExpanded ? 'translateY(0)' : 'translateY(calc(100% - 100px))',
        maxHeight: '100vh',
        zIndex: 20
      }}>
        {/* Drag Handle */}
        <div
          onClick={() => selectedStop ? clearStopSelection() : setSheetExpanded(!sheetExpanded)}
          style={{
            padding: '0.75rem',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#64748b',
            fontSize: '0.75rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {selectedStop ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                {selectedStop.name}
              </>
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: sheetExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
                {activeRoutesData.length} Active Routes
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{
          padding: '0 1rem 1rem',
          overflowY: 'auto',
          maxHeight: 'calc(60vh - 60px)'
        }}>
          {selectedStop ? (
            // Show stop arrivals
            <div>
              {loadingArrivals ? (
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid #e2e8f0',
                    borderTopColor: '#EAB308',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto'
                  }} />
                </div>
              ) : stopArrivals.length === 0 || stopArrivals[0]?.arrivals?.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>
                  No upcoming buses at this stop
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {stopArrivals.map((route) => (
                    <div key={route.routeId} style={{
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      backgroundColor: '#f8fafc',
                      border: `2px solid ${route.color}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          backgroundColor: route.color
                        }} />
                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{route.routeName}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {route.arrivals.map((arrival, i) => (
                          <div key={i} style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '0.375rem',
                            backgroundColor: i === 0 ? route.color : '#e2e8f0',
                            color: i === 0 ? '#ffffff' : '#1e293b',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {arrival.minutes === 0 ? 'Arriving' : `${arrival.minutes} min`}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Show active routes
            activeRoutesData.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>
                You're here early...
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: '0.75rem'
              }}>
                {activeRoutesData.map((routeData) => {
                  const routeColorName = routeData.displayName;
                  let displayColor = '#1E40AF';
                  const cat = getRouteColorName(routeData.description, routeData.color);
                  if (cat === 'RED') displayColor = '#DC2626';
                  else if (cat === 'BLUE') displayColor = '#1E40AF';
                  else if (cat === 'GREEN') displayColor = '#16A34A';
                  else if (cat === 'GOLD') displayColor = '#EAB308';
                  else if (cat === 'CLOUGH') displayColor = '#7C3AED';
                  else if (cat === 'NORTHSIDE') displayColor = '#D946EF';
                  else if (cat === 'EMORY') displayColor = '#EC4899';
                  else if (cat === 'NASA/SCIENCE') displayColor = '#06B6D4';
                  else if (cat === 'CHARTER') displayColor = '#F97316';
                  else if (cat === 'NIGHT GOLD') displayColor = '#6B7280';
                  else displayColor = routeData.color || '#6366f1';

                  const isVisible = visibleRoutes.has(String(routeData.routeId));

                  return (
                    <div
                      key={routeData.routeId}
                      onClick={() => toggleRoute(routeData.routeId)}
                      style={{
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: isVisible ? '#f1f5f9' : '#fafafa',
                        border: `2px solid ${isVisible ? displayColor : '#e2e8f0'}`,
                        opacity: isVisible ? 1 : 0.6,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: displayColor
                        }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1e293b' }}>
                          {routeColorName}
                        </span>
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
                        {routeData.vehicleCount}
                      </div>
                      <div style={{ fontSize: '0.625rem', color: '#64748b', textTransform: 'uppercase' }}>
                        buses
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
export default LiveMap;
